import { NgModule } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbNavModule,
  NgbPopover,
} from '@ng-bootstrap/ng-bootstrap';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { AgGridModule } from 'ag-grid-angular';
import { Ng2GoogleChartsModule } from 'ng2-google-charts';
import { ToastrModule } from 'ngx-toastr';

import {
  BarChartComponent,
  BarChartDateComponent,
  BaseComponent,
  BaseEditFormComponent,
  BaseEditFormModalComponent,
  BaseInlineFormComponent,
  BaseRouteComponent,
  ButtonRendererComponent,
  CheckboxRendererComponent,
  DeleteModalComponent,
  DiscardChangesModalComponent,
  FontLoaderComponent,
  FormlyArrayComponent,
  FormlyBackButtonComponent,
  FormlyButtonComponent,
  FormlyCheckboxComponent,
  FormlyDateComponent,
  FormlyHorizontalWrapperComponent,
  FormlyFieldsComponent,
  FormlyInlineWrapperComponent,
  FormlyInputComponent,
  FormlyInputSearchComponent,
  FormlyLineBreakComponent,
  FormlyRepeatComponent,
  FormlySelectComponent,
  FormlySummaryComponent,
  FormlyTemplateComponent,
  FormlyTextAreaComponent,
  FormOkCancelModalComponent,
  FormOkModalComponent,
  FormlyAddressLookupComponent,
  GridComponent,
  GridHeaderComponent,
  GridModalComponent,
  FileUploadComponent,
  LoadingComponent,
  OkCancelModalComponent,
  OkModalComponent,
  PieChartComponent,
  ReadonlyGridComponent,
  ReportFooterColumnsComponent,
  ReportFooterComponent,
  ReportGridComponent,
  ReportHeaderComponent,
  ReportHeaderDetailsComponent,
  ReportTableComponent,
  ReportTextComponent,
  ReportTitleComponent,
  ReportToolbarComponent,
  ReportValuePairsComponent,
  SearchGridComponent,
  SearchModalComponent,
  SideMenuComponent,
  SummaryComponent,
  SummaryTopComponent,
  TabComponent,
  TabFormModalComponent,
  TabGridComponent,
  TopMenuComponent,
  YesNoModalComponent,
  BaseRendererComponent,
  LanguageSelectComponent,
  AlertComponent,
  FormlyFileUploadComponent,
  MobileBaseRouteComponent,
  MobileMenuComponent,
  BaseFormComponent,
  FormlyRadioComponent,
  ReportImageComponent,
  DragAndDropComponent,
  EmailModalComponent,
  FormlyImportButtonComponent,
  DateFilterComponent,
  DateRendererComponent,
  PieChartModalComponent,
  LineChartComponent,
  GeoChartComponent,
  LineChartModalComponent,
  FormlyWarpWeftPlanComponent,
  SelectRendererComponent,
  ImageRendererComponent,
} from './components';
import { CustomNgbDateAdapter, CustomNgbDateFormatter } from './services';
import { TmsFormlyConfig } from './formly-config';
import { AuthModule } from '../auth';
import { DecimalValuePipe } from './pipes';
import { ReloadRouteReuseStrategy } from './helpers';

const formlyComponents = [
  FormlyCheckboxComponent,
  FormlyHorizontalWrapperComponent,
  FormlyInlineWrapperComponent,
  FormlyBackButtonComponent,
  FormlyButtonComponent,
  FormlyDateComponent,
  FormlyArrayComponent,
  FormlyFieldsComponent,
  FormlyFileUploadComponent,
  FormlyImportButtonComponent,
  FormlyInputComponent,
  FormlyInputSearchComponent,
  FormlyLineBreakComponent,
  FormlyAddressLookupComponent,
  FormlyRadioComponent,
  FormlyRepeatComponent,
  FormlySelectComponent,
  FormlySummaryComponent,
  FormlyTemplateComponent,
  FormlyTextAreaComponent,
  FormlyWarpWeftPlanComponent,
];

const chartComponents = [
  BarChartComponent,
  BarChartDateComponent,
  GeoChartComponent,
  LineChartComponent,
  PieChartComponent,
];

const gridComponents = [
  BaseRendererComponent,
  ButtonRendererComponent,
  CheckboxRendererComponent,
  DateRendererComponent,
  DateFilterComponent,
  ImageRendererComponent,
  SelectRendererComponent,
];

const mobileComponents = [MobileBaseRouteComponent, MobileMenuComponent];

const otherComponents = [
  AlertComponent,
  BaseComponent,
  BaseFormComponent,
  BaseEditFormComponent,
  BaseEditFormModalComponent,
  BaseInlineFormComponent,
  BaseRouteComponent,
  DiscardChangesModalComponent,
  DeleteModalComponent,
  DragAndDropComponent,
  EmailModalComponent,
  FontLoaderComponent,
  FormOkCancelModalComponent,
  FormOkModalComponent,
  GridComponent,
  GridHeaderComponent,
  GridModalComponent,
  FileUploadComponent,
  LoadingComponent,
  LanguageSelectComponent,
  LineChartModalComponent,
  OkCancelModalComponent,
  OkModalComponent,
  PieChartModalComponent,
  ReportToolbarComponent,
  ReadonlyGridComponent,
  ReportGridComponent,
  ReportFooterColumnsComponent,
  ReportFooterComponent,
  ReportHeaderComponent,
  ReportHeaderDetailsComponent,
  ReportImageComponent,
  ReportTableComponent,
  ReportTextComponent,
  ReportTitleComponent,
  ReportValuePairsComponent,
  SearchGridComponent,
  SearchModalComponent,
  SideMenuComponent,
  SummaryComponent,
  SummaryTopComponent,
  TabComponent,
  TabFormModalComponent,
  TabGridComponent,
  TopMenuComponent,
  YesNoModalComponent,
];

const components = [
  ...chartComponents,
  ...formlyComponents,
  ...gridComponents,
  ...mobileComponents,
  ...otherComponents,
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    AgGridModule,
    Ng2GoogleChartsModule,
    NgbDropdownModule,
    NgbDatepickerModule,
    NgbNavModule,
    NgSelectModule,
    RouterModule,
    FormlyBootstrapModule,
    FormlyModule.forRoot(TmsFormlyConfig),
    ToastrModule.forRoot({ positionClass: 'toast-bottom-center' }),
    AuthModule,
    NgbPopover,
  ],
  declarations: [...components, DecimalValuePipe],
  exports: [
    ...components,
    DecimalValuePipe,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    AgGridModule,
    Ng2GoogleChartsModule,
    FormlyModule,
  ],
  providers: [
    CurrencyPipe,
    DatePipe,
    DecimalValuePipe,
    DecimalPipe,
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateFormatter },
    { provide: NgbDateAdapter, useClass: CustomNgbDateAdapter },
    { provide: RouteReuseStrategy, useClass: ReloadRouteReuseStrategy },
  ],
})
export class TmsLibModule {}
